import React, { useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { sortRulesByPriority, describeCondition, describeAction } from '../../../utils/transformations/ruleTransformations';

const DecisionTreeView = ({ rules, fallbackPersona }) => {
  const { nodes, edges } = useMemo(() => {
    if (!rules || rules.length === 0) {
      return { nodes: [], edges: [] };
    }

    const sortedRules = sortRulesByPriority(rules);
    const nodes = [];
    const edges = [];

    // Start node
    nodes.push({
      id: 'start',
      type: 'input',
      position: { x: 250, y: 0 },
      data: { label: 'Assessment Data' },
      style: {
        background: '#e3f2fd',
        border: '2px solid #2196f3',
        padding: 10,
        borderRadius: 8
      }
    });

    // Rule nodes
    sortedRules.forEach((rule, index) => {
      const ruleId = `rule-${rule.priority}`;
      const yPosition = 100 + (index * 180);

      // Rule decision node
      nodes.push({
        id: ruleId,
        position: { x: 150, y: yPosition },
        data: {
          label: (
            <Box sx={{ minWidth: 200, maxWidth: 300 }}>
              <Chip
                label={`P${rule.priority}`}
                size="small"
                color={rule.isActive ? 'primary' : 'default'}
                sx={{ mb: 0.5 }}
              />
              <Typography variant="subtitle2" gutterBottom>
                {rule.name}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                {describeCondition(rule.conditions)}
              </Typography>
            </Box>
          )
        },
        style: {
          background: rule.isActive ? '#fff' : '#f5f5f5',
          border: `2px solid ${rule.isActive ? '#4caf50' : '#9e9e9e'}`,
          padding: 10,
          borderRadius: 8
        }
      });

      // Action result node
      const actionId = `action-${rule.priority}`;
      nodes.push({
        id: actionId,
        type: 'output',
        position: { x: 500, y: yPosition },
        data: {
          label: (
            <Box sx={{ minWidth: 180 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                {describeAction(rule.action)}
              </Typography>
            </Box>
          )
        },
        style: {
          background: '#e8f5e9',
          border: '2px solid #4caf50',
          padding: 10,
          borderRadius: 8
        }
      });

      // Edge from previous node to this rule
      if (index === 0) {
        edges.push({
          id: `start-${ruleId}`,
          source: 'start',
          target: ruleId,
          label: 'Evaluate',
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed }
        });
      } else {
        edges.push({
          id: `rule-${sortedRules[index - 1].priority}-${ruleId}`,
          source: `rule-${sortedRules[index - 1].priority}`,
          target: ruleId,
          label: 'No match',
          style: { stroke: '#ff9800' },
          markerEnd: { type: MarkerType.ArrowClosed }
        });
      }

      // Edge from rule to action
      edges.push({
        id: `${ruleId}-${actionId}`,
        source: ruleId,
        target: actionId,
        label: 'Match',
        style: { stroke: '#4caf50' },
        markerEnd: { type: MarkerType.ArrowClosed }
      });
    });

    // Fallback node
    const fallbackY = 100 + (sortedRules.length * 180);
    nodes.push({
      id: 'fallback',
      type: 'output',
      position: { x: 250, y: fallbackY },
      data: {
        label: (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
              Fallback
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              Assign {fallbackPersona || 'generalist'}
            </Typography>
          </Box>
        )
      },
      style: {
        background: '#fff3e0',
        border: '2px solid #ff9800',
        padding: 10,
        borderRadius: 8
      }
    });

    // Edge to fallback
    if (sortedRules.length > 0) {
      edges.push({
        id: `rule-${sortedRules[sortedRules.length - 1].priority}-fallback`,
        source: `rule-${sortedRules[sortedRules.length - 1].priority}`,
        target: 'fallback',
        label: 'No match',
        style: { stroke: '#ff9800' },
        markerEnd: { type: MarkerType.ArrowClosed }
      });
    } else {
      edges.push({
        id: 'start-fallback',
        source: 'start',
        target: 'fallback',
        label: 'No rules',
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed }
      });
    }

    return { nodes, edges };
  }, [rules, fallbackPersona]);

  if (!rules || rules.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No rules to display. Add rules to see the decision tree.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ height: '70vh', border: '1px solid #ddd', borderRadius: 1 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        minZoom={0.3}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
      >
        <Background color="#aaa" gap={16} />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </Box>
  );
};

export default DecisionTreeView;
